import type { Language } from '../types';
import { getTranslation } from './translations';
import { isAppInstalled, registerServiceWorker } from './pwaInstall';

const END_STORAGE_KEY = 'mealpai_scheduled_fasting_notification';
const START_STORAGE_KEY = 'mealpai_scheduled_fasting_start';

interface ScheduledFastingNotification {
    sessionId: string;
    endAtMs: number;
    goalHours: number;
    language: Language;
}

interface ScheduledFastingStartNotification {
    sessionId: string;
    startAtMs: number;
    goalHours: number;
    language: Language;
}

let activeEndTimeoutId: ReturnType<typeof setTimeout> | null = null;
let activeStartTimeoutId: ReturnType<typeof setTimeout> | null = null;

function clearStoredEndSchedule(): void {
    try {
        localStorage.removeItem(END_STORAGE_KEY);
    } catch {
        // ignore
    }
}

function clearStoredStartSchedule(): void {
    try {
        localStorage.removeItem(START_STORAGE_KEY);
    } catch {
        // ignore
    }
}

function readStoredEndSchedule(): ScheduledFastingNotification | null {
    try {
        const raw = localStorage.getItem(END_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as ScheduledFastingNotification;
        if (
            typeof parsed.sessionId !== 'string'
            || typeof parsed.endAtMs !== 'number'
            || typeof parsed.goalHours !== 'number'
        ) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function readStoredStartSchedule(): ScheduledFastingStartNotification | null {
    try {
        const raw = localStorage.getItem(START_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as ScheduledFastingStartNotification;
        if (
            typeof parsed.sessionId !== 'string'
            || typeof parsed.startAtMs !== 'number'
            || typeof parsed.goalHours !== 'number'
        ) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function writeStoredEndSchedule(payload: ScheduledFastingNotification): void {
    try {
        localStorage.setItem(END_STORAGE_KEY, JSON.stringify(payload));
    } catch {
        // ignore
    }
}

function writeStoredStartSchedule(payload: ScheduledFastingStartNotification): void {
    try {
        localStorage.setItem(START_STORAGE_KEY, JSON.stringify(payload));
    } catch {
        // ignore
    }
}

export type FastingNotificationPermission = NotificationPermission | 'unsupported';

export function canUseFastingNotifications(): boolean {
    return isAppInstalled();
}

export async function requestFastingNotificationPermission(): Promise<FastingNotificationPermission> {
    if (!canUseFastingNotifications()) {
        return 'unsupported';
    }
    if (typeof window === 'undefined' || !('Notification' in window)) {
        return 'unsupported';
    }
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    return Notification.requestPermission();
}

async function showFastingEndNotification(goalHours: number, language?: Language): Promise<void> {
    if (!canUseFastingNotifications()) return;

    const title = getTranslation(language, 'fastingNotificationTitle');
    const body = getTranslation(language, 'fastingNotificationBody').replace('{hours}', String(goalHours));

    await showFastingNotification(title, body, 'mealpai-fasting-end');
}

async function showFastingStartNotification(goalHours: number, language?: Language): Promise<void> {
    if (!canUseFastingNotifications()) return;

    const title = getTranslation(language, 'fastingStartNotificationTitle');
    const body = getTranslation(language, 'fastingStartNotificationBody').replace('{hours}', String(goalHours));

    await showFastingNotification(title, body, 'mealpai-fasting-start');
}

async function showFastingNotification(title: string, body: string, tag: string): Promise<void> {
    if (!canUseFastingNotifications()) return;

    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
        try {
            const registration = await navigator.serviceWorker.ready;
            await registration.showNotification(title, {
                body,
                tag,
                icon: '/icon-192.png',
                badge: '/icon-192.png',
            });
        } catch (error) {
            console.warn('Service worker notification failed', error);
        }
    }
}

export async function scheduleFastingEndNotification(params: {
    sessionId: string;
    endAtMs: number;
    goalHours: number;
    language?: Language;
}): Promise<void> {
    cancelFastingEndNotification();

    if (!canUseFastingNotifications()) return;

    if (typeof window === 'undefined' || !('Notification' in window)) return;

    await registerServiceWorker();

    const permission = await requestFastingNotificationPermission();
    if (permission !== 'granted') return;

    const payload: ScheduledFastingNotification = {
        sessionId: params.sessionId,
        endAtMs: params.endAtMs,
        goalHours: params.goalHours,
        language: params.language ?? 'en',
    };
    writeStoredEndSchedule(payload);

    const delay = Math.max(0, params.endAtMs - Date.now());
    activeEndTimeoutId = window.setTimeout(() => {
        activeEndTimeoutId = null;
        void showFastingEndNotification(payload.goalHours, payload.language);
        clearStoredEndSchedule();
    }, delay);
}

export function cancelFastingEndNotification(): void {
    if (activeEndTimeoutId != null) {
        window.clearTimeout(activeEndTimeoutId);
        activeEndTimeoutId = null;
    }
    clearStoredEndSchedule();
}

export async function scheduleFastingStartNotification(params: {
    sessionId: string;
    startAtMs: number;
    goalHours: number;
    language?: Language;
}): Promise<void> {
    cancelFastingStartNotification();

    if (!canUseFastingNotifications()) return;
    if (typeof window === 'undefined' || !('Notification' in window)) return;

    await registerServiceWorker();

    const permission = await requestFastingNotificationPermission();
    if (permission !== 'granted') return;

    const payload: ScheduledFastingStartNotification = {
        sessionId: params.sessionId,
        startAtMs: params.startAtMs,
        goalHours: params.goalHours,
        language: params.language ?? 'en',
    };
    writeStoredStartSchedule(payload);

    const delay = Math.max(0, params.startAtMs - Date.now());
    activeStartTimeoutId = window.setTimeout(() => {
        activeStartTimeoutId = null;
        void showFastingStartNotification(payload.goalHours, payload.language);
        clearStoredStartSchedule();
    }, delay);
}

export function cancelFastingStartNotification(): void {
    if (activeStartTimeoutId != null) {
        window.clearTimeout(activeStartTimeoutId);
        activeStartTimeoutId = null;
    }
    clearStoredStartSchedule();
}

export function resyncFastingEndNotification(
    activeSession: {
        id: string;
        startedAt: string;
        goalHours?: number;
    } | null,
    goalHours: number,
    language?: Language,
): void {
    if (!activeSession) {
        cancelFastingEndNotification();
        return;
    }

    if (!canUseFastingNotifications()) {
        cancelFastingEndNotification();
        return;
    }

    const sessionGoalHours = activeSession.goalHours ?? goalHours;
    const endAtMs = new Date(activeSession.startedAt).getTime() + sessionGoalHours * 60 * 60 * 1000;
    const stored = readStoredEndSchedule();

    if (stored?.sessionId === activeSession.id && stored.endAtMs === endAtMs && activeEndTimeoutId != null) {
        return;
    }

    if (Date.now() >= endAtMs) {
        checkMissedFastingEndNotification(activeSession.id);
        return;
    }

    void scheduleFastingEndNotification({
        sessionId: activeSession.id,
        endAtMs,
        goalHours: sessionGoalHours,
        language,
    });
}

export function resyncFastingStartNotification(
    scheduledSession: {
        id: string;
        scheduledStartAt?: string;
        goalHours?: number;
    } | null,
    goalHours: number,
    language?: Language,
): void {
    if (!scheduledSession?.scheduledStartAt) {
        cancelFastingStartNotification();
        return;
    }

    if (!canUseFastingNotifications()) {
        cancelFastingStartNotification();
        return;
    }

    const sessionGoalHours = scheduledSession.goalHours ?? goalHours;
    const startAtMs = new Date(scheduledSession.scheduledStartAt).getTime();
    const stored = readStoredStartSchedule();

    if (stored?.sessionId === scheduledSession.id && stored.startAtMs === startAtMs && activeStartTimeoutId != null) {
        return;
    }

    if (Date.now() >= startAtMs) {
        checkMissedFastingStartNotification(scheduledSession.id);
        return;
    }

    void scheduleFastingStartNotification({
        sessionId: scheduledSession.id,
        startAtMs,
        goalHours: sessionGoalHours,
        language,
    });
}

export function checkMissedFastingEndNotification(activeSessionId: string | null): void {
    if (!canUseFastingNotifications()) {
        cancelFastingEndNotification();
        return;
    }

    const stored = readStoredEndSchedule();
    if (!stored) return;

    if (activeSessionId && stored.sessionId !== activeSessionId) {
        cancelFastingEndNotification();
        return;
    }

    if (Date.now() < stored.endAtMs) return;

    void showFastingEndNotification(stored.goalHours, stored.language);
    clearStoredEndSchedule();
}

export function checkMissedFastingStartNotification(scheduledSessionId: string | null): void {
    if (!canUseFastingNotifications()) {
        cancelFastingStartNotification();
        return;
    }

    const stored = readStoredStartSchedule();
    if (!stored) return;

    if (scheduledSessionId && stored.sessionId !== scheduledSessionId) {
        cancelFastingStartNotification();
        return;
    }

    if (Date.now() < stored.startAtMs) return;

    void showFastingStartNotification(stored.goalHours, stored.language);
    clearStoredStartSchedule();
}
